/**
 * Home — workspace selector.
 *
 * Primera pantalla después del login. Muestra un grid de cards, una por
 * cliente al que el usuario tiene acceso. Cada card es el "workspace" de
 * ese cliente: click → /c/:slug.
 *
 * Acceso: filtramos por user.clientAccess (admin ve todos).
 */
import { Link } from "@tanstack/react-router";
import { ArrowUpRight, Inbox, BarChart3, Bell } from "lucide-react";
import { useCurrentUser } from "@/hooks/use-current-user";
import { useConversations } from "@/hooks/use-conversations";
import { CLIENTS, type ClientCard } from "@/lib/clients";
import { StatusDot } from "@/components/layout/status-dot";
import { cn } from "@/lib/cn";

const PLATFORM_LABEL: Record<string, string> = {
  instagram: "IG",
  facebook: "FB",
  whatsapp: "WA",
};

export function HomePage() {
  const { data: user } = useCurrentUser();
  const { data: conversations } = useConversations();

  const accessibleClients = CLIENTS.filter((c) => {
    if (!user) return false;
    if (user.role === "admin") return true;
    return user.clientAccess.includes(c.slug);
  });

  // Conteos cross-cliente para mostrar arriba.
  const totalActive = (conversations ?? []).filter(
    (c) => c.state === "bot" || c.state === "human" || c.state === "escalated",
  ).length;
  const totalEscalated = (conversations ?? []).filter(
    (c) => c.state === "escalated",
  ).length;
  const totalOverdue = (conversations ?? []).filter(
    (c) => c.slaRemainingSec < 0,
  ).length;

  function statsFor(slug: string) {
    const list = (conversations ?? []).filter((c) => c.client === slug);
    return {
      active: list.filter(
        (c) => c.state === "bot" || c.state === "human" || c.state === "escalated",
      ).length,
      escalated: list.filter((c) => c.state === "escalated").length,
      overdue: list.filter((c) => c.slaRemainingSec < 0).length,
    };
  }

  return (
    <div className="min-h-full bg-background overflow-y-auto">
      <div className="max-w-6xl mx-auto px-7 py-10">
        {/* Hero */}
        <header className="mb-10">
          <p className="font-mono text-xs uppercase tracking-wider text-text-muted mb-2">
            cm.champe
          </p>
          <h1 className="font-sans text-4xl font-semibold tracking-tight text-text-primary">
            Hola{user?.name ? `, ${user.name.split(" ")[0]}` : ""}
            <span className="text-magenta">.</span>
          </h1>
          <p className="font-sans text-md text-text-secondary mt-2">
            Elegí un workspace para arrancar.
          </p>

          {/* Global counters */}
          <div className="mt-6 flex items-center gap-2 flex-wrap">
            <GlobalChip
              icon={<Inbox size={14} strokeWidth={1.75} />}
              label="conversaciones activas"
              count={totalActive}
            />
            <GlobalChip
              icon={<ArrowUpRight size={14} strokeWidth={1.75} />}
              label="escaladas"
              count={totalEscalated}
              tone={totalEscalated > 0 ? "magenta" : "muted"}
            />
            <GlobalChip
              icon={<Bell size={14} strokeWidth={1.75} />}
              label="SLA vencidos"
              count={totalOverdue}
              tone={totalOverdue > 0 ? "warning" : "muted"}
            />
          </div>
        </header>

        {/* Grid de workspaces */}
        <section aria-label="Workspaces">
          <h2 className="section-label mb-3">Clientes</h2>
          {accessibleClients.length === 0 ? (
            <div className="card-surface p-8 text-center">
              <p className="font-sans text-sm text-text-muted">
                Tu usuario todavía no tiene clientes asignados. Hablá con un admin.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {accessibleClients.map((client) => (
                <WorkspaceCard
                  key={client.slug}
                  client={client}
                  stats={statsFor(client.slug)}
                />
              ))}
            </div>
          )}
        </section>

        {/* Atajos globales */}
        <section aria-label="Atajos" className="mt-12">
          <h2 className="section-label mb-3">Herramientas globales</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <ShortcutCard
              to="/sandbox"
              title="Sandbox"
              description="Correr CSVs contra cualquier skill"
              icon={<BarChart3 size={16} strokeWidth={1.5} />}
            />
            <ShortcutCard
              to="/audit"
              title="Audit log"
              description="Cadena verificable cross-cliente"
              icon={<ArrowUpRight size={16} strokeWidth={1.5} />}
            />
            {user?.role === "admin" && (
              <ShortcutCard
                to="/admin"
                title="Kill switch"
                description="Apagar el bot globalmente"
                icon={<Bell size={16} strokeWidth={1.5} />}
              />
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

interface WorkspaceCardProps {
  client: ClientCard;
  stats: { active: number; escalated: number; overdue: number };
}

function WorkspaceCard({ client, stats }: WorkspaceCardProps) {
  const statusVariant =
    client.health === "ok" ? "success" : client.health === "warn" ? "warning" : "muted";
  return (
    <Link
      to="/c/$clientSlug"
      params={{ clientSlug: client.slug }}
      className={cn(
        "card-surface p-5 block group",
        "hover:shadow-md hover:border-strong transition-all",
        "focus:outline-none focus:shadow-focus",
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <StatusDot variant={statusVariant} size={8} />
            <span className="font-sans text-2xs font-medium text-text-muted">
              {client.market}
            </span>
          </div>
          <h3 className="font-sans text-lg font-semibold text-text-primary">
            {client.fullName}
          </h3>
          <p className="font-sans text-xs text-text-secondary mt-0.5">
            {client.tagline}
          </p>
        </div>
        <ArrowUpRight
          size={16}
          strokeWidth={1.5}
          className="text-text-muted group-hover:text-magenta transition-colors shrink-0"
        />
      </div>

      {/* Plataformas */}
      <div className="flex items-center gap-1.5 mt-4">
        {client.platforms.map((p) => (
          <span
            key={p}
            className="inline-flex items-center rounded-full border border-default bg-surface-2 px-2 py-0.5 text-2xs font-sans font-medium text-text-secondary"
          >
            {PLATFORM_LABEL[p] ?? p}
          </span>
        ))}
      </div>

      {/* Stats */}
      <div className="mt-4 pt-4 border-t border-subtle grid grid-cols-3 gap-2">
        <Stat label="activas" value={stats.active} />
        <Stat
          label="escaladas"
          value={stats.escalated}
          highlight={stats.escalated > 0}
        />
        <Stat
          label="SLA −"
          value={stats.overdue}
          highlight={stats.overdue > 0}
        />
      </div>
    </Link>
  );
}

function Stat({
  label,
  value,
  highlight,
}: {
  label: string;
  value: number;
  highlight?: boolean;
}) {
  return (
    <div>
      <div
        className={cn(
          "font-mono text-base tabular-nums",
          highlight ? "text-magenta" : "text-text-primary",
        )}
      >
        {value}
      </div>
      <div className="font-sans text-xs text-text-muted mt-0.5">{label}</div>
    </div>
  );
}

interface GlobalChipProps {
  icon: React.ReactNode;
  label: string;
  count: number;
  tone?: "magenta" | "warning" | "muted";
}

function GlobalChip({ icon, label, count, tone = "muted" }: GlobalChipProps) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-3 py-1",
        "font-sans text-xs",
        tone === "magenta" &&
          "border-state-human-border bg-state-human-bg text-magenta",
        tone === "warning" &&
          "border-warning-border bg-warning-bg text-warning-text",
        tone === "muted" && "border-default bg-surface-2 text-text-secondary",
      )}
    >
      <span className="text-text-muted">{icon}</span>
      <span className="font-mono tabular-nums">{count}</span>
      <span>{label}</span>
    </span>
  );
}

interface ShortcutCardProps {
  to: string;
  title: string;
  description: string;
  icon: React.ReactNode;
}

function ShortcutCard({ to, title, description, icon }: ShortcutCardProps) {
  return (
    <Link
      to={to}
      className={cn(
        "card-surface p-4 block group",
        "hover:shadow-md hover:border-strong transition-all",
      )}
    >
      <div className="flex items-center gap-2 mb-1.5 text-text-muted group-hover:text-magenta transition-colors">
        {icon}
        <span className="font-sans text-sm font-medium text-text-primary">
          {title}
        </span>
      </div>
      <p className="font-sans text-xs text-text-secondary">{description}</p>
    </Link>
  );
}
